import { useParams, useLocation } from "react-router-dom";
import { Container } from "@shared/components/Container";
import { SeoHead } from "@shared/components";
import { useSubcategoriesByParent } from "@shared/hooks/useCategories";
import { useCategoryBySlug } from "@shared/hooks/useCategories";
import { slugToReadableName } from "@shared/utils";
import { generateBreadcrumbSchema } from "@shared/utils/schemaOrg";
import { Link } from "react-router-dom";
import type { CategoryWithOptionalImage } from "@shared/types/category";

export function Subcategories() {
  const { slugCatalogo, categoriaSlug } = useParams<{ 
    slugCatalogo: string;
    categoriaSlug: string;
  }>();
  const location = useLocation();
  const subcategoriesFromState = location.state?.subcategories;

  // 1. Obtener la categoría padre por slug para tener su ID
  const {
    data: parentCategory,
    isLoading: parentCategoryLoading,
    error: parentCategoryError,
  } = useCategoryBySlug(categoriaSlug || "");

  // 2. Obtener subcategorías por ID de la categoría padre
  const {
    data: subcategoriesData,
    isLoading: subcategoriesLoading,
    error: subcategoriesError,
  } = useSubcategoriesByParent(parentCategory?.id || "");

  // Usar subcategorías del estado o de la petición
  const subcategories = subcategoriesFromState || 
    (subcategoriesData?.data?.items || []);

  // Loading y error states
  const isLoading = parentCategoryLoading || subcategoriesLoading;
  const error = parentCategoryError || subcategoriesError;

  // Nombres para la UI
  const catalogName = slugToReadableName(slugCatalogo || "");
  const categoryName = parentCategory?.name || slugToReadableName(categoriaSlug || "");
  const pageTitle = `Subcategorías de ${categoryName}`;
  const pageDescription = parentCategory?.seoDescription || 
    `Explora las subcategorías de ${categoryName} en Cerámicas Gassull. Productos de construcción de alta calidad en Mendoza, Argentina.`;
  const canonicalUrl = `/catalog/${slugCatalogo}/categoria/${categoriaSlug}/subcategorias`;
  
  const breadcrumbItems = [
    { name: "Inicio", url: "/" },
    { name: slugToReadableName(slugCatalogo || ""), url: `/catalog/${slugCatalogo}` },
    { name: categoryName, url: `/catalog/${slugCatalogo}/categoria/${categoriaSlug}` },
    { name: "Subcategorías", url: canonicalUrl },
  ];
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbItems);

  return (
    <>
      <SeoHead
        category={parentCategory || undefined}
        title={pageTitle}
        description={pageDescription}
        keywords={parentCategory?.seoKeywords || undefined}
        canonical={canonicalUrl}
        schemaJsonLd={breadcrumbSchema}
      />
      <div className="min-h-screen">
      {/* Header de la página */}
      <section className="bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Breadcrumbs */}
            <nav className="text-sm text-gray-500 mb-4">
              <Link to="/" className="hover:text-gray-700">Inicio</Link>
              <span className="mx-2">/</span>
              <Link 
                to={`/catalog/${slugCatalogo}`} 
                className="hover:text-gray-700"
              >
                {catalogName}
              </Link>
              <span className="mx-2">/</span>
              <span className="text-gray-900">{categoryName}</span>
            </nav>
            
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {categoryName}
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Explora las subcategorías disponibles
            </p>
          </div>
        </div>
      </section>

      {/* Contenido principal */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className="h-56 bg-gray-200 rounded-lg animate-pulse"
                />
              ))}
            </div>
          ) : error ? (
            <Container variant="default" padding="lg" className="text-center">
              <div className="text-red-600">
                <p>Error al cargar las subcategorías: {error?.message}</p>
              </div>
            </Container>
          ) : subcategories.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {subcategories.map((subcategory: CategoryWithOptionalImage) => {
                // Imagen por defecto para subcategorías sin imagen
                const defaultImage =
                  "https://res.cloudinary.com/dq8ig7aly/image/upload/v1749660976/no-image_lbt2of.jpg";
                const backgroundImage = subcategory.image || defaultImage;

                return (
                  <Link
                    key={subcategory.id}
                    to={`/catalog/${slugCatalogo}/categoria/${categoriaSlug}/subcategoria/${subcategory.slug}`}
                    className="block"
                  >
                    <Container
                      variant="category-card"
                      background={backgroundImage}
                      className="h-56 flex flex-col justify-between group hover:scale-105 transition-transform duration-200"
                      padding="lg"
                    >
                      {/* Nombre de la subcategoría centrado */}
                      <div className="flex-1 flex items-center justify-center">
                        <h3 className="text-white font-bold text-lg text-center leading-tight">
                          {subcategory.name}
                        </h3>
                      </div>

                      {/* CTA en la esquina inferior derecha */}
                      <div className="flex justify-end">
                        <span className="text-white text-sm font-medium">
                          Ver productos →
                        </span>
                      </div>
                    </Container>
                  </Link>
                );
              })}
            </div>
          ) : (
            <Container variant="default" padding="lg" className="text-center">
              <p className="text-gray-600">
                No hay subcategorías disponibles en esta categoría.
              </p>
            </Container>
          )}
        </div>
      </section>
    </div>
    </>
  );
}
